import React, { useMemo, useState } from 'react';
import * as d3 from 'd3';
import { useDimensionsRef } from 'hooks/useDimensionsRef/useDimensionsRef';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';
import { useLaborCostDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/store/laborCostDashboard.store';
import {
  useChartColors,
  REPORTS_PAGE_COLORS,
} from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/LaborCostDashboard.styled';

const CHART_HEIGHT = 320;
const MARGIN = { top: 24, right: 20, bottom: 40, left: 44 };
const BAR_PADDING = 0.4;
const BAR_RX = 3;

export const CalloutFulfillmentRateChart: React.FC = () => {
  const data = useLaborCostDashboardStore((s) => s.calloutFulfillmentRate);
  const palette = useChartColors();
  const [containerRef, { width: containerWidth }] = useDimensionsRef<HTMLDivElement>();
  const tooltip = useChartTooltip();
  const [hovered, setHovered] = useState<string | null>(null);

  const barColor = palette.chartColors[3] || '#1C0F51';

  const innerWidth = Math.max(containerWidth - MARGIN.left - MARGIN.right, 0);
  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  const xScale = useMemo(
    () =>
      d3
        .scaleBand<string>()
        .domain(data?.map((d) => d.timeframe) ?? [])
        .range([0, innerWidth])
        .padding(BAR_PADDING),
    [data, innerWidth],
  );

  const yScale = useMemo(
    () => d3.scaleLinear().domain([0, 100]).range([innerHeight, 0]),
    [innerHeight],
  );

  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  if (!data) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return (
    <div ref={containerRef} style={{ width: '100%', minHeight: CHART_HEIGHT }}>
      {containerWidth > 0 && (
        <svg
          width={containerWidth}
          height={CHART_HEIGHT}
          aria-label="Fulfillment Rate of call out shifts by time of day"
        >
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {yTicks.map((tick) => (
              <g key={tick}>
                <line
                  x1={0}
                  x2={innerWidth}
                  y1={yScale(tick)}
                  y2={yScale(tick)}
                  stroke={REPORTS_PAGE_COLORS.gridLine}
                />
                <text
                  x={-8}
                  y={yScale(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize="12px"
                  fill={REPORTS_PAGE_COLORS.axisLabel}
                >
                  {tick}%
                </text>
              </g>
            ))}
            {data.map((d) => {
              const barX = xScale(d.timeframe) || 0;
              const barWidth = xScale.bandwidth();
              const centerX = barX + barWidth / 2;
              const barY = yScale(d.fulfillmentRate);
              const barHeight = innerHeight - barY;
              const rateText = formatDashboardNumber(d.fulfillmentRate);
              return (
                <g key={d.timeframe}>
                  <text
                    x={centerX}
                    y={barY - 8}
                    textAnchor="middle"
                    fontSize="13px"
                    fontWeight="600"
                    fill={REPORTS_PAGE_COLORS.textPrimary}
                  >
                    {rateText}%
                  </text>
                  <rect
                    x={barX}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={barColor}
                    rx={BAR_RX}
                    role="img"
                    aria-label={`${d.timeframe}: ${rateText}%`}
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    opacity={hovered === d.timeframe ? 0.8 : 1}
                    onMouseEnter={(e) => {
                      setHovered(d.timeframe);
                      tooltip.show(
                        e,
                        `<strong>${d.timeframe}</strong><br/>Fulfillment Rate: ${rateText}%`,
                      );
                    }}
                    onMouseMove={(e) => tooltip.move(e)}
                    onMouseLeave={() => {
                      setHovered(null);
                      tooltip.hide();
                    }}
                    onFocus={(e) => {
                      setHovered(d.timeframe);
                      tooltip.show(
                        e,
                        `<strong>${d.timeframe}</strong><br/>Fulfillment Rate: ${rateText}%`,
                      );
                    }}
                    onBlur={() => {
                      setHovered(null);
                      tooltip.hide();
                    }}
                  />
                  <text
                    x={centerX}
                    y={innerHeight + 20}
                    textAnchor="middle"
                    fontSize="12px"
                    fill={REPORTS_PAGE_COLORS.axisLabel}
                  >
                    {d.timeframe}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      )}
    </div>
  );
};
